import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, convertHTMLToRichText, FullColumn, RichTextViewer, Row, Typography } from 'seagull';
import { Checkmark } from 'seagull/icons';
import { useScrollBorder } from '~/hooks/useScrollBorder';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import styles from './SuggestedDescriptionDisplay.module.scss';
import { isPromptQuestion, suggestedDescriptionTextsPath, SuggestedDescriptionDisplayTestIds } from './SuggestedDescriptionDisplay.utils';
const SuggestedDescriptionDisplay = (param)=>{
    let { isLoading, isUndoLoading = false, suggestedDescription, hasServiceDescription, conversationId, canUndo, isClarification, onAccept, onInsert, onUndo, isInserting = false } = param;
    const { logBI } = useStoreActions();
    const { t: suggestedTexts } = useLocaleKeys(suggestedDescriptionTextsPath);
    const [hasServiceDescriptionAtInsert, setHasServiceDescriptionAtInsert] = useState(null);
    const contentRef = useRef(null);
    const parsedRichText = useMemo(()=>suggestedDescription != null ? convertHTMLToRichText(suggestedDescription) : null, [
        suggestedDescription
    ]);
    const { footerClassName: actionRowClassName } = useScrollBorder({
        contentRef,
        footerBaseClassName: styles.actionRow
    }, [
        suggestedDescription,
        isLoading,
        parsedRichText
    ]);
    useEffect(()=>{
        setHasServiceDescriptionAtInsert(null);
    }, [
        suggestedDescription
    ]);
    const handleInsertButtonClick = ()=>{
        if (parsedRichText && onInsert) {
            logBI(hasServiceDescription ? 17003 : 17002);
            onInsert(parsedRichText);
            setHasServiceDescriptionAtInsert(hasServiceDescription);
            conversationId !== '' && onAccept(conversationId);
        }
    };
    const handleUndoClick = ()=>{
        logBI(17008);
        onUndo();
    };
    const handleCopy = ()=>{
        if (suggestedDescription && !isPromptQuestion({
            description: suggestedDescription,
            promptText: suggestedTexts.promptQuestion()
        })) {
            const textCopied = isClarification ? suggestedDescription : (()=>{
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = suggestedDescription;
                return tempDiv.textContent || tempDiv.innerText || '';
            })();
            logBI(17010, {
                textCopied
            });
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Typography, {
            testId: SuggestedDescriptionDisplayTestIds.Loading(),
            variant: "paragraph1",
            weight: "normal",
            className: styles.textWithDots,
            children: suggestedTexts.loading()
        });
    }
    if (isClarification || isPromptQuestion({
        description: suggestedDescription,
        promptText: suggestedTexts.promptQuestion()
    })) {
        return /*#__PURE__*/ _jsx(RichTextViewer, {
            value: suggestedDescription !== null && suggestedDescription !== void 0 ? suggestedDescription : '',
            testId: SuggestedDescriptionDisplayTestIds.SuggestedDescription()
        });
    }
    return /*#__PURE__*/ _jsxs(FullColumn, {
        className: styles.root,
        justifyContent: "space-between",
        testId: SuggestedDescriptionDisplayTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(FullColumn, {
                className: styles.content,
                children: /*#__PURE__*/ _jsx("div", {
                    ref: contentRef,
                    className: styles.contentWrapper,
                    children: parsedRichText && /*#__PURE__*/ _jsx("div", {
                        className: styles.descriptionContent,
                        onCopy: handleCopy,
                        children: /*#__PURE__*/ _jsx(RichTextViewer, {
                            value: parsedRichText,
                            testId: SuggestedDescriptionDisplayTestIds.SuggestedDescription()
                        })
                    })
                })
            }),
            parsedRichText && !isPromptQuestion({
                description: suggestedDescription,
                promptText: suggestedTexts.promptQuestion()
            }) && /*#__PURE__*/ _jsxs(Row, {
                justifyContent: "end",
                gap: 8,
                className: actionRowClassName,
                children: [
                    canUndo && !isClarification && /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        size: "small",
                        testId: SuggestedDescriptionDisplayTestIds.UndoButton(),
                        onClick: handleUndoClick,
                        disabled: isInserting || isUndoLoading,
                        loading: isUndoLoading,
                        children: suggestedTexts.undo()
                    }),
                    (()=>{
                        const isInserted = hasServiceDescriptionAtInsert !== null;
                        const shouldShowReplaceButton = isInserted ? hasServiceDescriptionAtInsert : hasServiceDescription;
                        if (shouldShowReplaceButton) {
                            return /*#__PURE__*/ _jsx(Button, {
                                size: "small",
                                testId: SuggestedDescriptionDisplayTestIds.ReplaceButton(),
                                onClick: !isInserted ? handleInsertButtonClick : undefined,
                                prefixIcon: isInserted ? /*#__PURE__*/ _jsx(Checkmark, {
                                    size: 16
                                }) : undefined,
                                className: isInserted ? styles.insertedButton : undefined,
                                disabled: isUndoLoading,
                                children: isInserted ? suggestedTexts.replaced() : suggestedTexts.replace()
                            });
                        }
                        return /*#__PURE__*/ _jsx(Button, {
                            size: "small",
                            testId: SuggestedDescriptionDisplayTestIds.InsertButton(),
                            onClick: !isInserted ? handleInsertButtonClick : undefined,
                            prefixIcon: isInserted ? /*#__PURE__*/ _jsx(Checkmark, {
                                size: 16
                            }) : undefined,
                            className: isInserted ? styles.insertedButton : undefined,
                            disabled: isUndoLoading,
                            children: isInserted ? suggestedTexts.inserted() : suggestedTexts.insert()
                        });
                    })()
                ]
            })
        ]
    });
};
export default SuggestedDescriptionDisplay;
